import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Loader2, Brain } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { documentsAPI } from '../../utils/api';

/**
 * DocumentUploader - Unified document upload component with AI categorization
 *
 * This is the single source of truth for document uploads across the application.
 * Used in: IntakeWizard (DocumentationStep), CaseDetail (DocumentsSection)
 *
 * @param {number} caseId - Case ID to upload documents to
 * @param {Function} onDocumentUploaded - Called immediately when doc is uploaded (kind: 'pending')
 * @param {Function} onDocumentCategorized - Called when AI categorization completes
 * @param {Function} onAllComplete - Called when all uploads and categorizations are done
 * @param {boolean} showDropzone - Show dropzone style (default) or button-only style
 * @param {string} className - Additional CSS classes
 */
const DocumentUploader = ({
  caseId,
  onDocumentUploaded,
  onDocumentCategorized,
  onAllComplete,
  showDropzone = true,
  className,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [categorizingCount, setCategorizingCount] = useState(0);

  const uploadAndCategorize = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    const totalFiles = files.length;
    let uploadedCount = 0;
    const uploadedDocs = [];
    const errors = [];

    // Phase 1: Upload all files
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        // Don't set kind - backend defaults to 'pending'

        const result = await documentsAPI.uploadDocument(caseId, formData);

        if (result.document) {
          uploadedDocs.push({
            ...result.document,
            originalName: file.name,
          });

          // Notify parent immediately - document is now 'pending'
          if (onDocumentUploaded) {
            onDocumentUploaded(result.document);
          }
        }

        uploadedCount++;
        setUploadProgress((uploadedCount / totalFiles) * 100);
      } catch (error) {
        console.error('Error uploading file:', file.name, error);
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join(', ')
          : error.response?.data?.message || `Failed to upload ${file.name}`;
        errors.push(errorMessage);
      }
    }

    if (errors.length > 0) {
      toast.error(`Upload errors: ${errors.join('; ')}`);
    }

    if (uploadedCount > 0) {
      toast.info(`${uploadedCount} file(s) uploaded. AI is analyzing...`);
    }

    setUploading(false);
    setUploadProgress(0);

    // Phase 2: Categorize all uploaded documents
    if (uploadedDocs.length > 0) {
      setCategorizingCount(uploadedDocs.length);
      await categorizeDocuments(uploadedDocs);
      setCategorizingCount(0);

      if (onAllComplete) {
        onAllComplete();
      }
    }
  };

  const categorizeDocuments = async (docs) => {
    const results = { success: [], failed: [] };

    for (const doc of docs) {
      try {
        const result = await documentsAPI.categorizeDocument(doc.id);

        if (result.status === 'success') {
          results.success.push({
            id: doc.id,
            name: doc.originalName,
            category: result.kind_display,
            confidence: result.confidence,
          });

          // Notify parent with updated document
          if (onDocumentCategorized) {
            onDocumentCategorized({
              ...doc,
              kind: result.category,
              kind_display: result.kind_display,
              ai_confidence: result.confidence,
              ai_reasoning: result.reasoning,
            });
          }
        } else {
          results.failed.push(doc.originalName);
        }
      } catch (error) {
        console.error('Error categorizing document:', doc.originalName, error);
        results.failed.push(doc.originalName);
      }

      setCategorizingCount(prev => Math.max(0, prev - 1));
    }

    if (results.success.length > 0) {
      const categories = [...new Set(results.success.map(r => r.category))];
      toast.success(
        `AI categorized ${results.success.length} document(s): ${categories.join(', ')}`
      );
    }

    if (results.failed.length > 0) {
      toast.warning(`${results.failed.length} document(s) could not be categorized`);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    uploadAndCategorize(acceptedFiles);
  }, [caseId]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif'],
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    disabled: uploading,
    noClick: !showDropzone, // Allow click only in dropzone mode
    noKeyboard: !showDropzone,
  });

  const isProcessing = uploading || categorizingCount > 0;

  if (!showDropzone) {
    // Button-only mode (for compact areas)
    return (
      <div className={className}>
        <input {...getInputProps()} />
        <Button
          variant="outline"
          size="sm"
          onClick={open}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {uploading ? 'Uploading...' : `Analyzing (${categorizingCount})...`}
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload Files
            </>
          )}
        </Button>
      </div>
    );
  }

  // Dropzone mode
  return (
    <div className={cn('space-y-4', className)}>
      <div
        {...getRootProps()}
        className={cn(
          'relative cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-all',
          isDragActive && 'border-blue-500 bg-blue-50',
          !isDragActive && 'border-slate-300 hover:border-slate-400 hover:bg-slate-50',
          isProcessing && 'cursor-not-allowed opacity-70'
        )}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center gap-3">
          {isProcessing ? (
            <>
              <div className="relative">
                <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                {categorizingCount > 0 && (
                  <Brain className="h-4 w-4 text-purple-500 absolute -right-1 -bottom-1" />
                )}
              </div>
              <div>
                <p className="text-sm text-slate-600 font-medium">
                  {uploading
                    ? `Uploading... ${Math.round(uploadProgress)}%`
                    : `AI analyzing ${categorizingCount} document(s)...`}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Please wait
                </p>
              </div>
            </>
          ) : (
            <>
              <Upload className={cn(
                'h-10 w-10',
                isDragActive ? 'text-blue-500' : 'text-slate-400'
              )} />
              <div>
                <p className="text-sm text-slate-600">
                  {isDragActive
                    ? 'Drop files here...'
                    : 'Drag files here or click to select'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  PDF, PNG, JPG, WebP up to 50MB each
                </p>
                <p className="text-xs text-blue-500 mt-2 flex items-center justify-center gap-1">
                  <Brain className="h-3 w-3" />
                  AI will automatically categorize your documents
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upload progress bar */}
      {uploading && (
        <Progress value={uploadProgress} className="h-2" />
      )}
    </div>
  );
};

export default DocumentUploader;
